/**
 * The top bar's alert badge (phase 7b item 4): how many alerts hold now, in
 * the tone of the worst of them, as a link to the alerts page. Nothing shows
 * while there are none — or where the backend keeps no alerts (a node): the
 * container passes `counts: null`.
 */
import { BellIcon } from 'lucide-react';
import type { AlertCountsWire } from '@acp/api-contract/alerts.js';
import { badgeOf, severityTone } from '../../lib/alerts.js';
import { isPlainClick } from '../../lib/links.js';
import { Badge } from '../ui/badge.js';

export type AlertBadgeProps = {
  readonly counts: AlertCountsWire | null;
  readonly href: string;
  readonly onNavigate: (href: string) => void;
};

export function AlertBadge({ counts, href, onNavigate }: AlertBadgeProps) {
  if (counts === null) return null;
  const { total, worst } = badgeOf(counts);
  if (total === 0 || worst === null) return null;
  const detail = `${String(counts.critical)} critical, ${String(counts.warning)} warning, ${String(counts.info)} info`;
  return (
    <Badge variant={severityTone(worst)} asChild>
      <a
        href={href}
        data-testid="alert-badge"
        title={`Active alerts: ${detail}`}
        aria-label={`${String(total)} active alert${total === 1 ? '' : 's'}: ${detail}`}
        onClick={(event) => {
          if (!isPlainClick(event)) return;
          event.preventDefault();
          onNavigate(href);
        }}
      >
        <BellIcon aria-hidden="true" />
        {total}
      </a>
    </Badge>
  );
}
